import React, {useState, useEffect, Fragment, useRef} from 'react';
import {Button, Modal} from 'react-bootstrap';
import {useFormik} from 'formik';
import Editor from '@app/components/text-editor';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import {toast} from 'react-toastify';
import DropZone from '../../components/dropzone/DropZone';
import {checkEstimateTime, createIssue, createTask} from '@app/services/tasks';
import {getUserAssignedProjects} from '@app/services/project';
import handleStatus from '../../utils/ajax';
import {logoutUser} from '../../store/reducers/auth';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';

const EstimateInput = ({value, onChange, onBlur, isError, error}) => {
  return (
    <>
      <input
        type="text"
        className="form-control"
        name="estimateTime"
        placeholder={'Enter like 2h 30m'}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {isError && error ? <p className="text-danger">{error}</p> : null}
    </>
  );
};

const TaskForm = (props: any) => {
  const {mode, allUsers, setShow, getTasks, searchValues} = props;
  const [description, setDiscription] = useState('');
  const [assignedTo, setAssignedTo] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [projects, setProjects] = useState([]);
  const [disableProjects, setDisableProjects] = useState(true);
  const animatedComponents = makeAnimated();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [initialValues, setInitialValue] = useState({
    title: '',
    projectId: '',
    priority: '',
    estimateTime: '0h0m',
    description,
    assignedTo,
    issueType: ''
  });

  function validateTaskEstimate(inputValue, initialSave) {
    const regex = /^(\d{1,2}h(\d{1,2}m)?)?$/; // Regex to match XhYm format

    if (!inputValue.trim()) {
      return "Input can't be blank.";
    }

    if (!regex.test(inputValue)) {
      return 'Invalid format. Use XhYm, e.g., 2h30m.';
    }

    const [hours, minutes] = inputValue
      .split(/[hHmM]/)
      .filter(Boolean)
      .map(Number);

    if (hours > 4) {
      return 'Maximum task duration is 4 hours.';
    }

    if (minutes > 60) {
      return 'Minutes should not exceed 60.';
    }

    if (minutes > 1 && minutes < 10 && hours === 0) {
      return 'Minimum task duration is 10 minutes.';
    }

    return null; // Validation passed
  }

  const validate = (values) => {
    const errors = {};
    const {title, projectId, priority, estimateTime, issueType} = values;
    if (!issueType || issueType.trim() === '') {
      errors.issueType = 'Required!';
    }
    if (!title || title.trim() === '') {
      errors.title = 'Required!';
    }
    if (!projectId || projectId.trim() === '') {
      errors.projectId = 'Required!';
    }
    if (!priority || priority.trim() === '') {
      errors.priority = 'Required!';
    }

    if (
      description.trim() === '' ||
      description.trim().match(/[^'<p><br></p>']/g) === null
    ) {
      errors.description = 'Required!';
    }
    if (assignedTo.length === 0 || assignedTo === []) {
      errors.assignedTo = 'Required';
    }

    const resp = validateTaskEstimate(estimateTime, true);

    if (resp) {
      errors.estimateTime = resp;
    }

    return errors;
  };

  const removeHandler = (file: any) => {
    const {fileHash} = file;
    const filtered = attachments.filter(
      (item: any) => item.fileHash !== fileHash
    );
    toast.success('Removed Successfully.');
    setAttachments(filtered);
  };

  const onSubmit = async (values: any) => {
    if (assignedTo.length < 1) {
      toast.error('Please assign the task!');
      return;
    }
    if (
      description.trim() === '' ||
      description.trim().match(/[^'<p><br></p>']/g) === null
    ) {
      return;
    }

    const modifiedValues = {
      ...values,
      description,
      assignedTo: [assignedTo.value]
    };

    setDisableSubmit(true);
    const resp = await createTask(modifiedValues);
    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);

    if (resp.status === 200) {
      const {projectId, _id} = resp.data.data;

      if (attachments.length > 0) {
        const filesResp = attachments.map((items: any) =>
          createIssue({
            hash: items.fileHash,
            fileName: items.fileName,
            projectId: projectId,
            taskId: _id
          })
        );

        const promises = await Promise.all(filesResp);

        const failed = promises.filter((p) => p.status !== 200);

        if (failed.length === 0) {
          getTasks(searchValues);
          toast.success('Task created');
          setShow(false);
          return;
        }
      }
      getTasks(searchValues);
      toast.success('Task created');
      setShow(false);
    }
    setDisableSubmit(false);
  };

  const handleAssigneeSelection = async (user) => {
    setAssignedTo(user);
    setDisableProjects(true);
    try {
      const resp = await getUserAssignedProjects(user.value);
      if (resp.status !== 200) {
        toast.error('Unable to fetch user assigned projects');
        setDisableProjects(false);
      }

      setProjects(resp.data.data);
      setDisableProjects(false);
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const {values, errors, touched, handleSubmit, handleBlur, handleChange} =
    useFormik({
      initialValues,
      validate,
      onSubmit
    });

  return (
    <Fragment>
      <Modal
        {...props}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show
        onHide={props.onhide}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <div className="card-body">
              <div className="form-group">
                <div className="col-lg my-2">
                  <label>Issue Type</label>
                  <select
                    name="issueType"
                    className="form-control"
                    value={values.issueType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option defaultChecked value=""></option>
                    <option value="task">Task</option>
                    <option value="bug">Bug</option>
                    <option value="testing">Testing</option>
                    <option value="r&d">R&D</option>
                  </select>

                  {touched.issueType && errors.issueType ? (
                    <p className="text-danger">{errors.issueType}</p>
                  ) : null}
                </div>
                <div className="col-lg my-2">
                  <label>Title </label>
                  <input
                    type="title"
                    placeholder="Enter title"
                    className="form-control"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.title && errors.title ? (
                    <p className="text-danger">{errors.title}</p>
                  ) : null}
                </div>
                <div className="col-lg  my-2">
                  <label>Description</label>
                  <Editor onChange={(e) => setDiscription(e)} />
                  {errors.description ? (
                    <p className="text-danger">{errors.description}</p>
                  ) : null}
                </div>

                <div className="col-lg my-4 ">
                  <label>Upload Files</label>
                  <DropZone
                    setAttachments={setAttachments}
                    removeHandler={removeHandler}
                    attachments={attachments}
                    mode={'Create'}
                  />
                </div>
                <div className="col-lg  my-2">
                  <label>Assigned To</label>

                  <Select
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    defaultValue={[]}
                    value={assignedTo}
                    onChange={handleAssigneeSelection}
                    options={allUsers}
                    onBlur={handleBlur}
                  />
                  {errors.assignedTo ? (
                    <p className="text-danger">{errors.assignedTo}</p>
                  ) : null}
                </div>
                <div className="col-lg  my-2">
                  <label>Project</label>
                  <select
                    name="projectId"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={disableProjects}
                  >
                    <option defaultValue="">--None--</option>
                    {projects.map((project: any, index: any) => (
                      <option key={index} value={project._id}>
                        {project.projectName}
                      </option>
                    ))}
                  </select>{' '}
                  {touched.projectId && errors.projectId ? (
                    <p className="text-danger">{errors.projectId}</p>
                  ) : null}
                </div>
                <div className="col-lg my-2">
                  <label>Priority</label>
                  <select
                    name="priority"
                    className="form-control"
                    defaultValue={values.priority}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">--None--</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  {touched.priority && errors.priority ? (
                    <p className="text-danger">{errors.priority}</p>
                  ) : null}
                </div>
                <div className="col-lg my-2">
                  <label>Estimate Time</label>
                  <EstimateInput
                    value={values.estimateTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isError={touched.estimateTime}
                    error={errors.estimateTime}
                  />
                  {/* <input
                    type="text"
                    className="form-control"
                    name="estimateTime"
                    placeholder={'Enter like 2h 30m'}
                    value={values.estimateTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.estimateTime && errors.estimateTime ? (
                    <p className="text-danger">{errors.estimateTime}</p>
                  ) : null} */}
                </div>
                <div className="col-lg mt-4 d-flex">
                  <Button
                    disabled={disableSubmit}
                    className="mx-3"
                    type="submit"
                    variant="primary"
                  >
                    {mode}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default TaskForm;
